import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import {
  computeBudgetTotals,
  fiscalYearOf,
  groupTransactionsByAccountCode,
  partitionBudgetCodes,
  type BudgetCode,
  type BudgetTotals,
  type NewTransaction,
  type Transaction,
} from '@budgetmonkey/shared'
import { useCurrentUser } from '@/auth/AuthProvider'
import { useResolvedFiscalYear } from '@/lib/fiscalYear'
import { loadStateOf } from '@/lib/loadState'
import { useBudgetCodes, useCodesById } from '@/features/budgetCodes/hooks'
import { queryKeys } from '@/lib/queryClient'
import {
  createTransaction,
  deleteTransaction,
  mergeCharges,
  fetchTransactions,
  fetchTransactionsByIds,
  updateTransaction,
  type TransactionUpdate,
} from './api'
import { withDeadline } from '@/lib/withTimeout'

/** All transactions for the fiscal year. Every other view filters this. */
export function useTransactions(explicitYear?: number) {
  const fiscalYear = useResolvedFiscalYear(explicitYear)
  const user = useCurrentUser()
  return useQuery({
    queryKey: queryKeys.transactions(user.uid, fiscalYear),
    queryFn: () => withDeadline(fetchTransactions(user.uid, fiscalYear)),
  })
}

/**
 * Specific charges by id, whatever year they are in — for a reconciliation
 * packet, whose cycle straddles the July rollover. See `fetchTransactionsByIds`.
 *
 * Keyed on the sorted ids so two periods asking for the same charges share a
 * result and a reorder does not refetch.
 */
export function useTransactionsByIds(ids: readonly string[]) {
  const user = useCurrentUser()
  const key = useMemo(() => [...ids].sort(), [ids])
  return useQuery({
    queryKey: ['transactionsByIds', user.uid, key],
    queryFn: () => fetchTransactionsByIds(key),
    enabled: key.length > 0,
  })
}

/**
 * A single transaction, read live from the cached year.
 *
 * Detail views must select by ID rather than hold a captured object: an edit
 * made on the detail screen invalidates the query, and a held snapshot would
 * keep rendering the pre-edit values until the user navigated away and back.
 */
export function useTransaction(
  id: string | undefined,
  explicitYear?: number,
) {
  const fiscalYear = useResolvedFiscalYear(explicitYear)
  const query = useTransactions(fiscalYear)
  const data = useMemo(
    () => query.data?.find((t) => t.id === id),
    [query.data, id],
  )
  return { ...query, data }
}

/** Transactions on one budget code — filtered in memory, no extra round trip. */
export function useTransactionsForCode(
  budgetCodeId: string | undefined,
  explicitYear?: number,
) {
  const fiscalYear = useResolvedFiscalYear(explicitYear)
  const query = useTransactions(fiscalYear)
  const codesById = useCodesById()
  const data = useMemo(() => {
    if (budgetCodeId === undefined) return []
    // Match by account code, the same grouping the dashboard uses, not by the
    // document id. "The same code" is a per-year document, so a charge filed
    // against another year's Travel document still belongs on this year's
    // Travel — they share an account code. Filtering by document id here made
    // this page show "no transactions" on a code the dashboard credited with
    // nine, after copying codes into a past year gave that year its own Travel
    // document the charges had never been filed against.
    const accountCode = codesById.get(budgetCodeId)?.code ?? budgetCodeId
    const byAccountCode = groupTransactionsByAccountCode(
      query.data ?? [],
      (id) => codesById.get(id)?.code,
    )
    return byAccountCode.get(accountCode) ?? []
  }, [query.data, budgetCodeId, codesById])
  return { ...query, data }
}

export interface CodeWithTotals {
  code: BudgetCode
  totals: BudgetTotals
}

/**
 * Owned budget codes with their computed totals — the dashboard's data.
 *
 * Totals are derived client-side from the year's transactions rather than
 * stored as counters (§7.4 design note): at these volumes it's fast, and a
 * derived number cannot drift out of sync with the ledger the way a
 * denormalized one can.
 */
export function useOwnedCodeTotals(explicitYear?: number) {
  const fiscalYear = useResolvedFiscalYear(explicitYear)
  const codesQuery = useBudgetCodes(fiscalYear)
  const transactionsQuery = useTransactions(fiscalYear)
  const codesById = useCodesById()

  const data = useMemo<CodeWithTotals[]>(() => {
    const { owned } = partitionBudgetCodes(codesQuery.data ?? [])
    const transactions = transactionsQuery.data ?? []
    // Grouped by account code, not by document id — see
    // groupTransactionsByAccountCode. The code detail page groups the same way,
    // through the same helper, so the two screens cannot disagree about which
    // charges land on a code.
    const byAccountCode = groupTransactionsByAccountCode(
      transactions,
      (id) => codesById.get(id)?.code,
    )
    return owned
      .map((code) => ({
        code,
        totals: computeBudgetTotals(
          code.beginningBalance,
          byAccountCode.get(code.code) ?? [],
        ),
      }))
      .sort((a, b) => a.code.code.localeCompare(b.code.code))
  }, [codesQuery.data, transactionsQuery.data, codesById])

  return {
    data,
    // `state`, not `isLoading`: a query paused for want of a connection is
    // pending with nothing in flight, so `isLoading` reads false and the
    // screen would render its empty state over data it never asked for.
    // See lib/loadState.
    state: loadStateOf(codesQuery, transactionsQuery),
    error: codesQuery.error ?? transactionsQuery.error,
  }
}

/**
 * Transactions filed against external codes (§8.2b), with their code attached.
 * These deliberately never reach any balance calculation.
 */
export function useExternalTransactions(explicitYear?: number) {
  const fiscalYear = useResolvedFiscalYear(explicitYear)
  const codesQuery = useBudgetCodes(fiscalYear)
  const transactionsQuery = useTransactions(fiscalYear)

  const data = useMemo(() => {
    const externalIds = new Set(
      (codesQuery.data ?? []).filter((c) => c.external).map((c) => c.id ?? ''),
    )
    const byId = new Map((codesQuery.data ?? []).map((c) => [c.id ?? '', c]))
    return (transactionsQuery.data ?? [])
      .filter((t) => externalIds.has(t.budgetCodeId))
      .map((t) => ({ transaction: t, code: byId.get(t.budgetCodeId) }))
  }, [codesQuery.data, transactionsQuery.data])

  return {
    data,
    // `state`, not `isLoading`: a query paused for want of a connection is
    // pending with nothing in flight, so `isLoading` reads false and the
    // screen would render its empty state over data it never asked for.
    // See lib/loadState.
    state: loadStateOf(codesQuery, transactionsQuery),
    error: codesQuery.error ?? transactionsQuery.error,
  }
}

function useInvalidate(fiscalYear: number) {
  const user = useCurrentUser()
  const client = useQueryClient()
  return () =>
    client.invalidateQueries({
      queryKey: queryKeys.transactions(user.uid, fiscalYear),
    })
}

/**
 * Invalidate a year decided at save time rather than at render time — a
 * charge's year comes from its purchase date, so the cache to refresh is not
 * known until the form is submitted.
 */
function useInvalidateYear() {
  const user = useCurrentUser()
  const client = useQueryClient()
  return (fiscalYear: number) =>
    client.invalidateQueries({
      queryKey: queryKeys.transactions(user.uid, fiscalYear),
    })
}

/**
 * Capture a charge into the fiscal year its purchase falls in — §11, which
 * names a year by its ending year and rolls it over on 1 July.
 *
 * Not the year it happens to be captured in. A P-Card cycle straddles the
 * rollover every July: the statement dated late July carries charges from the
 * last days of June, and those belong to the year that just ended. Stamping
 * "today" filed them under the new year, where they inflated a balance that
 * had nothing to do with them — and it would have done so again every July.
 *
 * The year is therefore derived at save time from `transactionDate`, which is
 * also why the cache invalidation cannot be fixed at render time: the list to
 * refresh is not known until the date is.
 */
export function useCreateTransaction() {
  const user = useCurrentUser()
  const client = useQueryClient()
  const invalidateYear = useInvalidateYear()
  return useMutation({
    mutationFn: async (input: NewTransaction) => {
      const fiscalYear = fiscalYearOf(new Date(input.transactionDate))
      const created = await createTransaction(user.uid, fiscalYear, input)
      return { id: created.id ?? '', fiscalYear, created }
    },
    /**
     * The charge goes into the cached year immediately, and the refetch is
     * started rather than awaited.
     *
     * Awaiting it is what made filing sit on "Filing…" for four round trips.
     * But closing the panel over a *stale* list is its own bug: the receipt
     * stayed under "Waiting to be filed", which reads as a failed save, and
     * filing it again created a second charge for one purchase — every
     * duplicate check blind to the charge that already existed because it was
     * not in the cache either. So the cache is corrected here and the refetch
     * confirms it afterwards.
     */
    onSuccess: ({ fiscalYear, created }) => {
      client.setQueryData<Transaction[]>(
        queryKeys.transactions(user.uid, fiscalYear),
        (current) =>
          current === undefined || current.some((t) => t.id === created.id)
            ? current
            : [created, ...current].sort((a, b) =>
                b.transactionDate.localeCompare(a.transactionDate),
              ),
      )
      void invalidateYear(fiscalYear)
    },
  })
}

/**
 * Edit a charge.
 *
 * Refreshes the year being viewed and, when the edit moved the charge to a
 * different budget year, that year too. Refreshing only one leaves the charge
 * showing in the year it left until something else happens to invalidate it —
 * and a total that still counts a charge moved out of it is exactly the wrong
 * number to leave on screen.
 */
export function useUpdateTransaction(explicitYear?: number) {
  const fiscalYear = useResolvedFiscalYear(explicitYear)
  const user = useCurrentUser()
  const invalidate = useInvalidate(fiscalYear)
  const invalidateYear = useInvalidateYear()
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: TransactionUpdate }) =>
      updateTransaction(id, patch),
    // The edit is written into the cached lists here, and the refetches are
    // started rather than awaited — see the note on `useLinkReceipt`. A save
    // that waits for every list to come back before it closes is a save that
    // looks hung on a slow connection; one that closes over the old values
    // looks like a save that did not happen.
    onSuccess: (_result, { id, patch }) => {
      client.setQueriesData<Transaction[]>(
        { queryKey: ['transactions', user.uid] },
        (current) => {
          if (current === undefined) return current
          const movedOut =
            patch.fiscalYear !== undefined &&
            current.some((t) => t.id === id && t.fiscalYear !== patch.fiscalYear)
          // A charge whose year changed leaves the list it was in rather than
          // sitting there with the new year's values until the refetch.
          if (movedOut) return current.filter((t) => t.id !== id)
          return current.map((t) => (t.id === id ? { ...t, ...patch } : t))
        },
      )
      void invalidate()
      if (patch.fiscalYear !== undefined && patch.fiscalYear !== fiscalYear) {
        void invalidateYear(patch.fiscalYear)
      }
      // A packet reads its charges by id, outside any year's list.
      void client.invalidateQueries({ queryKey: ['transactionsByIds'] })
    },
  })
}

/**
 * Fold one charge into another — see `mergeCharges`.
 *
 * Invalidates receipts as well as charges: the merge moves documents between
 * charges, and the receipts screen decides what is filed from the charge side.
 */
export function useMergeCharges(explicitYear?: number) {
  const fiscalYear = useResolvedFiscalYear(explicitYear)
  const user = useCurrentUser()
  const invalidate = useInvalidate(fiscalYear)
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ keepId, removeId }: { keepId: string; removeId: string }) =>
      mergeCharges(user.uid, keepId, removeId),
    onSuccess: () => {
      void invalidate()
      void client.invalidateQueries({ queryKey: ['receipts', user.uid] })
      void client.invalidateQueries({ queryKey: ['transactionsByIds'] })
    },
  })
}

export function useDeleteTransaction(explicitYear?: number) {
  const fiscalYear = useResolvedFiscalYear(explicitYear)
  const user = useCurrentUser()
  const invalidate = useInvalidate(fiscalYear)
  return useMutation({
    mutationFn: (id: string) => deleteTransaction(user.uid, id),
    onSuccess: invalidate,
  })
}
